import { useLayoutEffect, useRef, useState } from 'react'

/** Hold presentation only; the operation and its success/error state remain immediate. */
export function useMinimumProgress(active: boolean, minimumMs: number): boolean {
  const [held, setHeld] = useState(active)
  // undefined = idle; null = mounted, waiting for its first display frame.
  const startedAt = useRef<number | null | undefined>(undefined)

  useLayoutEffect(() => {
    if (active) {
      startedAt.current = null
      setHeld(true)
      // Start the minimum at a display frame, not before React has mounted/painted
      // the indicator. Otherwise a busy commit can eat into its visible duration.
      const frame = window.requestAnimationFrame(() => { startedAt.current = performance.now() })
      return () => window.cancelAnimationFrame(frame)
    }
    if (startedAt.current === undefined) return
    const elapsed = startedAt.current === null ? 0 : performance.now() - startedAt.current
    const remaining = Math.max(0, minimumMs - elapsed)
    const finish = () => { startedAt.current = undefined; setHeld(false) }
    if (remaining === 0) { finish(); return }
    const timer = window.setTimeout(finish, remaining)
    return () => window.clearTimeout(timer)
  }, [active, minimumMs])

  return active || held
}

/** Preserve what was actually running while its visual minimum finishes. */
export function useMinimumProgressLabel(label: string | undefined, minimumMs: number): string | undefined {
  const visible = useMinimumProgress(Boolean(label), minimumMs)
  const [lastLabel, setLastLabel] = useState(label)
  useLayoutEffect(() => { if (label) setLastLabel(label) }, [label])
  return label ?? (visible ? lastLabel : undefined)
}
